import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import Weather, { IconSpeakerMuted, IconSpeakerOn } from "./weather";
import { getStoryVideoUrl } from "./videoSources";
import "./Showcase.css";

const MARQUEE_CHUNK = "✧˖° WEATHER JARS ";

const JAR_DROPS = [
  { city: "Paris,FR", title: "Paris — France" },
  { city: "Berlin,DE", title: "Berlin — Germany" },
  { city: "Jakarta", title: "Jakarta — Indonesia" },
];

function InstagramIcon() {
  return (
    <svg
      className="showcase__insta-icon"
      viewBox="0 0 24 24"
      width="22"
      height="22"
      aria-hidden
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"
      />
    </svg>
  );
}

const Showcase = () => {
  const [lightbox, setLightbox] = useState(null);
  const [mutedByCity, setMutedByCity] = useState(() =>
    Object.fromEntries(JAR_DROPS.map((j) => [j.city, true]))
  );
  const lightboxStageRef = useRef(null);
  const [lightboxLayoutTick, setLightboxLayoutTick] = useState(0);
  /** Which story-section jar has sound: `null` = both muted, exclusive unmute. */
  const [storyAudioJar, setStoryAudioJar] = useState(null);
  const storyVideoLeftRef = useRef(null);
  const storyVideoRightRef = useRef(null);
  const marquee = Array(24).fill(MARQUEE_CHUNK).join("");

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox]);

  useLayoutEffect(() => {
    if (!lightbox) return;
    setLightboxLayoutTick((v) => v + 1);
  }, [lightbox]);

  const toggleStoryJarSound = useCallback((side) => {
    setStoryAudioJar((current) => (current === side ? null : side));
  }, []);

  /** Enlarge one jar; mute the other grid players so only the opened jar can have sound. */
  const openLightbox = useCallback((jar) => {
    setMutedByCity((prev) => {
      const next = {};
      JAR_DROPS.forEach((j) => {
        next[j.city] = j.city === jar.city ? false : true;
      });
      return next;
    });
    setLightbox({ city: jar.city, title: jar.title });
  }, []);

  useEffect(() => {
    const left = storyVideoLeftRef.current;
    const right = storyVideoRightRef.current;
    if (left) {
      const p = left.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
    if (right) {
      const p = right.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [storyAudioJar]);

  return (
    <div className="showcase">
      <div className="showcase__marquee" aria-hidden="true">
        <div className="showcase__marquee-inner">
          <span>{marquee}</span>
          <span>{marquee}</span>
        </div>
      </div>

      <div className="showcase__stars">✦ ✦ ✦</div>

      <header className="showcase__hero">
        <div className="showcase__hero-intro">
          <h1 className="showcase__title">The Weather Jars</h1>
          <p className="showcase__byline">by Dream Team (Zenavi × v i v)</p>
        </div>
        <div className="showcase__hero-jars" id="drops">
          <p className="showcase__live-ribbon" role="status">
            <span className="showcase__live-dot" aria-hidden="true" />
            <span className="showcase__live-label">Live</span>
            <span className="showcase__live-sep" aria-hidden="true">
              ·
            </span>
            <span className="showcase__live-detail">Art & sound changes with real-time weather and time of day</span>
          </p>
          <div className="showcase__grid">
            {JAR_DROPS.map((jar) => (
              <article key={jar.city} className="showcase__card">
                <div className="showcase__jar">
                  <Weather
                    city={jar.city}
                    preview
                    dockExpanded={lightbox && lightbox.city === jar.city}
                    lightboxStageRef={lightboxStageRef}
                    lightboxLayoutTick={lightboxLayoutTick}
                    muted={mutedByCity[jar.city]}
                    onMutedChange={(m) =>
                      setMutedByCity((prev) => ({ ...prev, [jar.city]: m }))
                    }
                    hideAudioButton={lightbox && lightbox.city === jar.city}
                    onArtworkClick={() => openLightbox(jar)}
                  />
                </div>
                <div className="showcase__card-body">
                  <h3 className="showcase__card-title">{jar.title}</h3>
                </div>
              </article>
            ))}
          </div>
        </div>
        <div className="showcase__hero-intro">
          <p className="showcase__lead">
          A Weather Jar is a living digital artwork that changes according to the real-time weather and time of day of a specific city.

          </p>
          <p className="showcase__lead">
          Rain, sunlight, clouds, storms, and nightfall are reflected through evolving visuals and sound, allowing each jar to continuously transform throughout the day.

          </p>
          <p className="showcase__lead">
          More than just art, Weather Jars become vessels for memory, distance, and connection. 
          </p>
        </div>
      </header>

      <section className="showcase__story">
        <h2 className="showcase__section-heading showcase__section-heading--story">
          What is the story behind Weather Jars?
        </h2>
        <div className="showcase__story-inner">
          <div className="showcase__story-row showcase__story-row--lead">
            <div className="showcase__story-media showcase__story-media--left">
              <video
                ref={storyVideoLeftRef}
                className="showcase__story-video"
                src={getStoryVideoUrl("jar_compiled_2.mp4")}
                autoPlay
                muted={storyAudioJar !== "left"}
                loop
                playsInline
                tabIndex={0}
                title={storyAudioJar === "left" ? "Click to mute sound" : "Click to play with sound"}
                aria-label={
                  storyAudioJar === "left"
                    ? "Weather Jar alternate view, sound on. Click to mute."
                    : "Weather Jar alternate view. Click to play with sound."
                }
                onClick={() => toggleStoryJarSound("left")}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault();
                  toggleStoryJarSound("left");
                }}
              />
            </div>
            <div className="showcase__story-copy">
              <p>
                The story behind the creation of Weather Jars is deeply personal and rooted in the artist&apos;s own experiences and emotions. Growing up in Indonesia, the artist found herself frequently checking the weather not for local conditions, but as a means of staying connected to her family abroad. Each weather report became a window into the lives of loved ones living in different countries, offering glimpses into their daily routines and activities.
              </p>
              <p>
                One particular memory stands out: the artist&apos;s grandfather, a baker, would ride his bike early in the mornings to the bakery to set everything up. During snowy weather, the artist would anxiously check the forecast, hoping her grandfather would be safe on his journey. This ritual of checking the weather to ensure the well-being of loved ones became a poignant symbol of connection and care.
              </p>
            </div>
          </div>
          <div className="showcase__story-row showcase__story-row--rest">
            <div className="showcase__story-copy">
              <p>
                Inspired by these experiences, the artist stemmed the idea of Weather Jars as a way to honor the bonds between individuals separated by distance. Each jar represents a specific country or city, serving as a tangible reminder of cherished memories or loved ones living abroad. Whether it&apos;s checking in on family members or reminiscing about past adventures, Weather Jars offer a sense of comfort and connection, making the world feel a little bit smaller and more intimate.
              </p>
              <p>
                By infusing each jar with personal meaning and emotion, the artist hopes to create a sense of resonance and intimacy for viewers, allowing them to feel a deeper connection to the artwork and to each other. In this way, Weather Jars become more than just decorative pieces—they become vessels of love, nostalgia, and connection, bridging the gap between distance and longing.
              </p>
            </div>
            <div className="showcase__story-media showcase__story-media--right">
              <video
                ref={storyVideoRightRef}
                className="showcase__story-video"
                src={getStoryVideoUrl("jar_compiled.mp4")}
                autoPlay
                muted={storyAudioJar !== "right"}
                loop
                playsInline
                tabIndex={0}
                title={storyAudioJar === "right" ? "Click to mute sound" : "Click to play with sound"}
                aria-label={
                  storyAudioJar === "right"
                    ? "Weather Jar preview, sound on. Click to mute."
                    : "Weather Jar preview. Click to play with sound."
                }
                onClick={() => toggleStoryJarSound("right")}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault();
                  toggleStoryJarSound("right");
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* <section className="showcase__features">
        <h2 className="showcase__section-title">Why it resonates</h2>
        <div className="showcase__feature-grid">
          <div className="showcase__feature">
            <h3>Jar of memories</h3>
            <p>
              Dynamic scenes shift with the real day: morning, noon, and night, paired with
              conditions from clear skies to storms—always tied to a real place on the map.
            </p>
          </div>
          <div className="showcase__feature">
            <h3>Emotional resonance</h3>
            <p>
              Sound and motion invite nostalgia and presence—remembering a trip, or picturing
              a loved one&apos;s sky where they are now.
            </p>
          </div>
          <div className="showcase__feature">
            <h3>Collaborative artistry</h3>
            <p>
              Illustration, animation, composition, and interaction come together as one
              piece—art and craft in the same jar.
            </p>
          </div>
        </div>
      </section> */}

      <section className="showcase__team">
        <h2 className="showcase__section-heading showcase__section-heading--team">The Dream Team</h2>
        <p className="showcase__team-lead">
        The team behind “Weather Jars” unites visual art, sound, and technology through the collaboration of Zenavi and v i v.
        </p>
        <p className="showcase__team-lead">
        Together, they create immersive digital experiences that blend storytelling, real-time technology, visuals, music, and emotion. Zenavi shapes the visual worlds and narratives, while v i v develops the coding and soundscapes that bring each jar to life.
        </p>
        <div className="showcase__bios">
          <div className="showcase__bio">
            <img
              className="showcase__bio-photo"
              src={`${process.env.PUBLIC_URL}/zenavi_prof.png`}
              alt="Portrait of Zenavi"
              width={120}
              height={120}
            />
            <h4 className="showcase__bio-name">Zenavi</h4>
            <p className="role">Artist · Illustrator · 2D Animator</p>
            <p>
              Zenavi, a visionary artist with a background shaped by a life of travel and cultural
              exploration, brings a unique perspective to each piece of the &quot;Weather Jars&quot;
              collection. Drawing inspiration from her experiences growing up and moving from country
              to country, Zeni infuses each artwork with deep sentimental value and personal significance.
            </p>
            <a
              className="showcase__bio-insta"
              href="https://www.instagram.com/zenaviart/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Zenavi on Instagram"
            >
              <InstagramIcon />
            </a>
          </div>
          <div className="showcase__bio">
            <img
              className="showcase__bio-photo"
              src={`${process.env.PUBLIC_URL}/viv_prof.png`}
              alt="Portrait of v i v"
              width={120}
              height={120}
            />
            <h4 className="showcase__bio-name">v i v</h4>
            <p className="role">Composer · Sound Designer</p>
            <p>
              v i v, a masterful composer and sound designer renowned for their ability to evoke emotion
              and atmosphere through music. With a keen understanding of the power of sound to enhance
              visual storytelling, v i v&apos;s compositions breathe life into the &quot;Weather
              Jars&quot; collection, transporting viewers to immersive worlds filled with texture, depth,
              and emotion.
            </p>
            <a
              className="showcase__bio-insta"
              href="https://www.instagram.com/vivifymusic/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="v i v on Instagram"
            >
              <InstagramIcon />
            </a>
          </div>
        </div>
      </section>

      <footer className="showcase__footer">
        <span className="glyph">✦ ✦ ✦</span>
        <p>Thank you for spending time with Weather Jars.</p>
        <p style={{ marginTop: "0.5rem", fontSize: "0.88rem" }}>
          If this work speaks to you, follow the artists for more.
        </p>
      </footer>

      {lightbox ? (
        <div
          className="showcase__lightbox"
          role="dialog"
          aria-modal="true"
          aria-labelledby="showcase-lightbox-title"
          onClick={() => setLightbox(null)}
        >
          <div className="showcase__lightbox-panel" onClick={(e) => e.stopPropagation()}>
            <div className="showcase__lightbox-bar">
              <div className="showcase__lightbox-bar-start">
                <button
                  type="button"
                  className="showcase__lightbox-close"
                  aria-label="Close"
                  title="Close"
                  onClick={() => setLightbox(null)}
                >
                  <svg
                    className="showcase__lightbox-close-icon"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <path
                      d="M7 7l10 10M17 7l-10 10"
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
              <div className="showcase__lightbox-title-cluster">
                <h2 id="showcase-lightbox-title" className="showcase__lightbox-title">
                  {lightbox.title}
                </h2>
              </div>
              <div className="showcase__lightbox-bar-end">
                <button
                  type="button"
                  className={`showcase__lightbox-audio${
                    mutedByCity[lightbox.city]
                      ? " showcase__lightbox-audio--muted"
                      : " showcase__lightbox-audio--live"
                  }`}
                  aria-label={mutedByCity[lightbox.city] ? "Unmute video" : "Mute video"}
                  title={mutedByCity[lightbox.city] ? "Unmute" : "Mute"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMutedByCity((prev) => ({
                      ...prev,
                      [lightbox.city]: !prev[lightbox.city],
                    }));
                  }}
                >
                  {mutedByCity[lightbox.city] ? (
                    <IconSpeakerMuted size={22} />
                  ) : (
                    <IconSpeakerOn size={22} />
                  )}
                </button>
              </div>
            </div>
            <div ref={lightboxStageRef} className="showcase__lightbox-stage" />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Showcase;
